import { get } from 'lodash';
import * as React from 'react';
import {
    Controller,
    type FieldError,
    type FieldPathValue,
    type FieldValues,
    type Path,
} from 'react-hook-form';
import { YStack } from 'tamagui';

import { Picker } from '../inputs/Picker';
import { LabelRegular } from '../text/Typography';
import type { BaseControlledProps } from './ControlledInput';

interface ControlledPickerProps<
    TFieldValues extends FieldValues,
    TContext,
> extends BaseControlledProps<TFieldValues, TContext> {
    onPressDone?: (
        selectedValue: FieldPathValue<TFieldValues, Path<TFieldValues>>,
    ) => void;
    options: Array<{
        label: string;
        value: FieldPathValue<TFieldValues, Path<TFieldValues>>;
        enabled?: boolean;
    }>;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    size?: 'regular' | 'tiny';
}

// TODO - Fix issue where the placeholder option is a valid value for the Picker, which should not be the case.
export const ControlledPicker = <TFieldValues extends FieldValues, TContext>({
    onPressDone,
    options,
    name,
    control,
    register,
    errors,
    label,
    placeholder,
    disabled,
    size,
}: ControlledPickerProps<TFieldValues, TContext>) => {
    // Use lodash get to access nested values in dot notation
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...register,
            }}
            render={({ field: { onChange, value } }) => (
                <YStack gap={12}>
                    {label != null && (
                        <LabelRegular color={isError ? '$red1100' : '$black'}>
                            {label}
                        </LabelRegular>
                    )}
                    <Picker
                        value={value}
                        onChange={onChange}
                        options={options}
                        onPressDone={onPressDone}
                        placeholder={placeholder}
                        disabled={disabled}
                        size={size}
                    />
                    {isError && (
                        <LabelRegular color="$red1000">
                            {error?.message}
                        </LabelRegular>
                    )}
                </YStack>
            )}
        />
    );
};
